import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Compass, Home } from 'lucide-react';

/**
 * Iter-157a — Friendly catch-all fallback.
 *
 * Before this component, any unmatched URL (stale deep-links in old
 * notification emails, typos, or race-y redirects) rendered an entirely
 * blank page because React Router silently matches nothing. This is the
 * fix: any `*` route lands here and gets a clear explanation + a way
 * back to their role-appropriate home.
 */
const roleHome = (role) => {
  switch (role) {
    case 'talent':      return '/talent/home';
    case 'client':      return '/client/dashboard';
    case 'admin':
    case 'super_admin': return '/admin/home';
    case 'agency':      return '/agency/dashboard';
    default:            return '/login';
  }
};

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let role = null;
  try {
    const raw = localStorage.getItem('user');
    role = raw ? JSON.parse(raw)?.role : null;
  } catch {
    // Ignored — user not logged in or malformed storage.
  }
  const homePath = roleHome(role);

  return (
    <div
      className="min-h-screen bg-gray-50 flex items-center justify-center px-4"
      data-testid="not-found-page"
    >
      <Card className="max-w-md w-full border border-gray-200 shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="mx-auto w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
            <Compass className="w-6 h-6 text-cyan-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Page not found</h1>
          <p className="text-sm text-gray-600 mb-6">
            The link you followed may be expired, moved, or no longer available.
          </p>
          <p className="text-[11px] text-gray-400 mb-6 break-all">{location.pathname}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={() => navigate(homePath)}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="not-found-home-btn"
            >
              <Home className="w-4 h-4 mr-1" /> Back to home
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              data-testid="not-found-back-btn"
            >
              Go back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotFound;
